import { useState, useEffect, createContext } from "react";
import { getMainView, getViewerView, getSideViewerView, callApi, setCookie, getCookie } from "./Helpers";
import { useParams } from "react-router-dom";
import Viewer from "./components/Viewer";
import MainBody from "./components/MainBody";
import Backdrop from "./components/Backdrop";
import SideViewer from "./components/SideViewer";
import ConfirmDialog from "./components/ConfirmDialog";
import { ToastContainer, toast } from 'react-toastify';
import SideBar from "./components/SideBar";
import TopBar from "./components/TopBar";
import MainMenuBtn from "./components/MainMenuBtn";
import Login from "./views/Login";
import OverlayLoader from "./components/OverlayLoader";
import MainLoader from "./components/MainLoader";

export const AppContext = createContext(null);

export default function App(props) {

  const [ready, setReady] = useState(false); //for checking if app is ready

  const { _navItem, _navSubItem, _navExtraItem, _navMoreItem } = useParams(); //for routing purposes

  const [navItem, setNavItem] = useState(_navItem); //routing 
  const [navSubItem, setNavSubItem] = useState(_navSubItem); //routing
  const [navExtraItem, setNavExtraItem] = useState(_navExtraItem); //routing
  const [navMoreItem, setNavMoreItem] = useState(_navMoreItem); //routing

  const [showViewer, setShowViewer] = useState(false); //controlling the display of Viewer component
  const [viewerView, setViewerView] = useState(null); //the view to be shown in viewer

  const [showSideViewer, setShowSideViewer] = useState(false); //controlling the display of SideViewer component
  const [sideViewerView, setSideViewerView] = useState(null); //the view to be shown in viewer

  const [mainView, setMainView] = useState(null); //the view tobe shown in MainBody

  const [showOverlayLoader, setShowOverlayLoader] = useState(false); //controlling the display of OverlayLoader

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
  const [confirmDialogAction, setConfirmDialogAction] = useState("");

  const [isSideBarOpen, setIsSideBarOpen] = useState(false); //controls open and close for sidebar in mobile devices

  const [auth, setAuth] = useState(false); //track user authorization status

  //currentSession
  const [currentUserData, setCurrentUserData] = useState(null);
  const [currentSchoolUuid, setCurrentSchoolUuid] = useState(null);
  const [currentSchoolData, setCurrentSchoolData] = useState(null);

  //App level data
  const [systemParams, setSystemParams] = useState(null);
  const [allClassrooms, setAllClassrooms] = useState(null);
  const [allStreams, setAllStreams] = useState(null);
  const [allSubjects, setAllSubjects] = useState(null);
  const [allGeneralPaymentCycles, setAllGeneralPaymentCycles] = useState(null);
  const [allIndividualPaymentCycles, setAllIndividualPaymentCycles] = useState(null);
  const [allPaymentRecords, setAllPaymentRecords] = useState(null);
  const [allStudents, setAllStudents] = useState(null);
  const [allStaff, setAllStaff] = useState(null);
  const [allGradingSystems, setAllGradingSystems] = useState(null);
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [dashboardCounts, setDashboardCounts] = useState(null);

  const [mySubjects, setMySubjects] = useState(null);
	const [myExamCycles, setMyExamCycles] = useState(null);
	const [myClassrooms, setMyClassrooms] = useState(null);
	const [myStreams, setMyStreams] = useState(null);

 
  async function init() {
    /**
     * Initialize the app here
     */

    setReady(false);
    await getSystemParams().then(async () => {
      await authCheck().then(async (_auth) => {
        if (_auth) {
          await getCurrentUserData();

        }
      })

      //turn off loaders after initialization
      setReady(true);
    })
  }

  async function getSystemParams() {
    return new Promise(async resolve => {
      await callApi('get-system-params', {}).then(response => {
        if (response.status === 1) {
          setSystemParams(response.data);
          resolve(response.data)
        } else {
          resolve(null)
        }
      })
    })
  }

  function navBack() {
    window.history.back();
    setShowOverlayLoader(false);
  }

  function tellError(msg) {
    toast.error(msg);
  }

  function tellInfo(msg) {
    toast.info(msg);
  }

  function tellWarning(msg) {
    toast.warn(msg);
  }

  function tellMessage(msg) {
    toast.success(msg);
  }

  function refresh() {
    /**
     * This function refreshes the whole app
     */
    window.location.reload(); //remember to optimize
  }

  function navTo(nav) {
    /**
     * This function handles navigation inside the app
     * Utilizing Hash based routing
     * nav is the object supporting the following keys: item, subItem, extraItem, moreItem
     */
    if (nav) {
      //..

      let url = '';
      if (nav.item) {
        url = `#/${nav.item}/`
      }

      if (nav.subItem) {
        url += `${nav.subItem}/`
      }

      if (nav.extraItem) {
        url += `${nav.extraItem}/`
      }

      if (nav.moreItem) {
        url += `${nav.moreItem}/`
      }

      window.location.href = url;
      //..
    }
  }

  async function getCurrentUserData() {
    await callApi('get-current-user-data', {}).then(response => {
      if (response.status === 1) {
        setCurrentUserData(response.data);

        //set current school
        const schoolUuids = JSON.parse(response.data.schoolUuids);

        if(schoolUuids.length > 0) {
          setCurrentSchoolUuid(schoolUuids[schoolUuids.length - 1]);
        }
      }
    })
  }

  async function getCurrentSchoolData() {
    if(currentSchoolUuid) {
      await callApi('get-school-data', { uuid: currentSchoolUuid }).then(response => {
        if (response.status === 1) {
          setCurrentSchoolData(response.data);
        }
      })
    }
  }

  function authCheck() {
    return new Promise(async resolve => {
      await callApi('auth-check', {}).then(response => {
        if (response.status === 1) {
          setAuth(true);
          resolve(true)
        } else {
          setAuth(false);
          resolve(false);
        }
      })
    })
  }

  function activateDialog(params) {
    let {
      message,
      onConfirm
    } = params;
    setConfirmDialogAction(() => { return onConfirm });
    setConfirmDialogMessage(message)
    setShowConfirmDialog(true);
  }

  async function getAllClassrooms() {
    await callApi("get-all-classrooms", { schoolUuid: currentSchoolUuid }).then(response => {
      if (response.status === 1) {
        setAllClassrooms(response.data);
      }
    })
  }

  async function getAllStreams() {
    await callApi("get-all-streams", { schoolUuid: currentSchoolUuid }).then(response => {
      if (response.status === 1) {
        setAllStreams(response.data);
      }
    })
  }

  async function getDashboardCounts() {
    await callApi("get-dashboard-counts", { schoolUuid: currentSchoolUuid }).then(response => {
      if (response.status === 1) {
        setDashboardCounts(response.data);
      }
    })
  }

  async function getTotalExpenses() {
    await callApi("get-total-expenses", { schoolUuid: currentSchoolUuid }).then(response => {
      if (response.status === 1) {
        setTotalExpenses(response.data);
      }
    })
  }

  async function getTotalIncome() {
    await callApi("get-total-income", { schoolUuid: currentSchoolUuid }).then(response => {
      if (response.status === 1) {
        setTotalIncome(response.data);
      }
    })
  }

  async function getAllSubjects() {
    await callApi("get-all-subjects", { schoolUuid: currentSchoolUuid }).then(response => {
      if (response.status === 1) {
        setAllSubjects(response.data);
      }
    })
  }

  async function getAllGradingSystems() {
    await callApi("get-all-grading-systems", { schoolUuid: currentSchoolUuid }).then(response => {
      if (response.status === 1) {
        setAllGradingSystems(response.data);
      }
    })
  }

  async function getAllStudents() {
    await callApi("get-all-students", { schoolUuid: currentSchoolUuid }).then(response => {
      if (response.status === 1) {
        setAllStudents(response.data);
      }
    })
  }

  async function getAllStaff() {
    await callApi("get-all-staff", { schoolUuid: currentSchoolUuid }).then(response => {
      if (response.status === 1) {
        setAllStaff(response.data);
      }
    })
  }

  async function getAllGeneralPaymentCycles() {
    await callApi("get-all-general-payment-cycles", { schoolUuid: currentSchoolUuid }).then(response => {
      if (response.status === 1) {
        setAllGeneralPaymentCycles(response.data);
      }
    })
  }

  async function getAllIndividualPaymentCycles() {
    await callApi("get-all-individual-payment-cycles", { schoolUuid: currentSchoolUuid }).then(response => {
      if (response.status === 1) {
        setAllIndividualPaymentCycles(response.data);
      }
    })
  }

  async function getAllPaymentRecords() {
    await callApi("get-all-payment-records", { schoolUuid: currentSchoolUuid }).then(response => {
      if (response.status === 1) {
        setAllPaymentRecords(response.data);
      }
    })
  }

  async function getMySubjects() {
    await callApi("get-my-subjects", { schoolUuid: appContext.currentSchoolUuid }).then(response => {
      if(response.status === 1) {
        setMySubjects(response.data);
      }
    })
  }

  async function getMyExamCycles() {
    await callApi("get-my-exam-cycles", { schoolUuid: appContext.currentSchoolUuid }).then(response => {
      if(response.status === 1) {
        setMyExamCycles(response.data);
      }
    })
  }

  async function getMyClassrooms() {
    await callApi("get-my-classrooms", { schoolUuid: appContext.currentSchoolUuid }).then(response => {
      if(response.status === 1) {
        setMyClassrooms(response.data);
      }
    })
  }

  async function getMyStreams() {
    await callApi("get-my-streams", { schoolUuid: appContext.currentSchoolUuid }).then(response => {
      if(response.status === 1) {
        setMyStreams(response.data);
      }
    })
  }

  useEffect(() => {
    init();
  }, [])

  useEffect(() => {
    if (isSideBarOpen) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }
  }, [isSideBarOpen])

  useEffect(() => {
    setNavItem(_navItem);
    setNavSubItem(_navSubItem);
    setNavExtraItem(_navExtraItem);
    setNavMoreItem(_navMoreItem);
  }, [_navItem, _navSubItem, _navExtraItem, _navMoreItem])

  useEffect(() => {
    if (currentUserData) {
      //handle current user data changes here
    }
  }, [currentUserData])
  
  useEffect(() => {

  }, [ currentSchoolData ])

  useEffect(() => {
    getCurrentSchoolData();
  
  }, [ currentSchoolUuid ])

  useEffect(() => {

    //check for viewers
    if (navItem === 'view') {
      //activate viewer
      setShowViewer(true);
      setViewerView(getViewerView(appContext));

      //hide other viewers
      setShowSideViewer(false)
      setSideViewerView(null);

    } else if (navItem === 'side-view') {
      //activate viewer
      setShowSideViewer(true);
      setSideViewerView(getSideViewerView(appContext));

      //hide other viewers
      //setShowViewer(false)
      //setViewerView(null);

    } else {
      //just set normal views
      setShowViewer(false);
      setShowSideViewer(false)

      setViewerView(null);
      setSideViewerView(null);

      const _mainView = getMainView(appContext)
      if (_mainView) {
        setMainView(_mainView);
      }
    }
  }, [navItem, navSubItem, navExtraItem, navMoreItem]);

  const appContext = {
    refresh,
    navTo,
    mainView,
    viewerView,
    setShowOverlayLoader,
    navItem,
    navSubItem,
    navExtraItem,
    navMoreItem,
    setShowViewer,
    showViewer,
    navBack,
    showSideViewer,
    setShowSideViewer,
    sideViewerView,
    activateDialog,
    setShowConfirmDialog,
    confirmDialogAction,
    confirmDialogMessage,
    showConfirmDialog,
    tellError,
    tellInfo,
    tellMessage,
    tellWarning,
    isSideBarOpen,
    setIsSideBarOpen,
    showOverlayLoader,
    auth,
    currentUserData,
    systemParams,
    getSystemParams,
    currentSchoolUuid,
    setCurrentSchoolUuid,
    currentSchoolData,
    getCurrentSchoolData,
    getAllClassrooms,
    allClassrooms,
    getAllSubjects,
    allSubjects,
    allGeneralPaymentCycles,
    getAllGeneralPaymentCycles,
    allIndividualPaymentCycles,
    getAllIndividualPaymentCycles,
    allPaymentRecords,
    getAllPaymentRecords,
    allStreams,
    getAllStreams,
    getAllStudents,
    allStudents,
    allStaff,
    getAllStaff,
    getAllGradingSystems,
    allGradingSystems,
    mySubjects,
    getMySubjects,
    myExamCycles,
    getMyExamCycles,
    myClassrooms,
    getMyClassrooms,
    myStreams,
    getMyStreams,
    totalExpenses,
    totalIncome,
    getTotalExpenses,
    getTotalIncome,
    getDashboardCounts,
    dashboardCounts,
  }

  if (ready) {
    if (auth) {
      if (currentUserData) {
        return (
          <AppContext.Provider value={appContext}>
            <MainBody />
            <TopBar />
            <MainMenuBtn />
            <SideBar />
            <Viewer />
            <SideViewer />
            <ConfirmDialog />
            <OverlayLoader />
            <ToastContainer position="bottom-right" />
          </AppContext.Provider>
        )
      } else {
        return (
          <AppContext.Provider value={appContext}>
            <ToastContainer position="bottom-right" />
            <MainLoader />
          </AppContext.Provider>
        )
      }
    } else {
      return (
        <AppContext.Provider value={appContext}>
          <OverlayLoader />
          <ToastContainer position="bottom-right" />
          <Login />
        </AppContext.Provider>
      )
    }
  } else {
    return (
      <AppContext.Provider value={appContext}>
        <ToastContainer position="bottom-right" />
        <MainLoader />
      </AppContext.Provider>
    )
  }
}